import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "./root.store";
import type {
  ReportingApprovalRequestItem,
  ReportingConnectionStatus,
  ReportingConversationSummary,
  ReportingInputRequestItem,
  ReportingMessageItem,
  ReportingTranscriptItem,
} from "@/types/reporting-agent";
import { projectTranscript } from "@/lib/reporting-agent/transcript";

type TurnStatus = "idle" | "updating" | "success" | "failed";

// If the server's `ready` frame doesn't arrive within this window of a socket
// opening, the connect is treated as failed and retried on a fresh socket
// (NG-1269: a `ready` can be lost when a transient slug change replaces the
// socket mid-handshake, leaving the composer disabled forever with no error).
const CONNECT_READY_TIMEOUT_MS = 5_000;
// Initial attempt plus two reconnects. After this the agent is treated as
// unreachable and the error banner is surfaced instead of a silent dead composer.
const MAX_CONNECT_ATTEMPTS = 3;

/**
 * Reporting-agent chat state machine (one live WebSocket conversation).
 *
 * Owns the observable conversation as a single ordered transcript of items —
 * text bubbles, `ask_user` question boxes, and `request_approval` plan boxes,
 * all inline and retained (a pending box stays in place and becomes read-only
 * once answered). Plus the in-flight streaming message and the tool indicator.
 * The transport (`IReportingAgentSocket`) is injected via the root store, so
 * this class holds no socket lifecycle and is unit-testable with a fake socket.
 */
export class ReportingAgentStore {
  connectionStatus: ReportingConnectionStatus = "disconnected";
  sessionId: string | undefined = undefined;
  items: ReportingTranscriptItem[] = [];
  streamingMessageId: string | undefined = undefined;
  toolRunning = false;
  status: TurnStatus = "idle";
  /** True after a `4401` handshake close — the caller routes the user to login. */
  authFailed = false;

  /** The caller's past conversations, for the history picker. */
  conversations: ReportingConversationSummary[] = [];
  conversationsLoading = false;

  // A dashboard_ready artifact id received mid-turn, held until turn_complete so
  // the link surfaces only once the turn finishes.
  private pendingDashboardArtifactId: string | null = null;

  // The conversation id we're resuming: sent as a `resume` frame after the fresh
  // connect's `ready`, cleared once the server re-binds to it.
  private pendingResumeId: string | null = null;

  // Connect-retry control (NG-1269). `connectTimer` fires if `ready` doesn't
  // arrive in time; `connectingSlug` is what we redial; `connectAttempts` bounds
  // the retries. Non-observable — internal control state, not view state.
  private connectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectingSlug: string | null = null;
  private connectAttempts = 0;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        connectTimer: false,
        connectingSlug: false,
        connectAttempts: false,
      } as never,
      { autoBind: true },
    );
  }

  /** True while the composer must stay disabled: not connected, a turn is
   *  streaming/running, or a question/plan box is awaiting the user's reply. */
  get inputDisabled(): boolean {
    return (
      this.connectionStatus !== "connected" ||
      !!this.streamingMessageId ||
      this.status === "updating" ||
      this.awaitingResponse
    );
  }

  /** True while an inline question/plan box is still unanswered. */
  get awaitingResponse(): boolean {
    return this.items.some(
      (item) =>
        (item.kind === "input_request" && item.answer === null) ||
        (item.kind === "approval_request" && item.approved === null),
    );
  }

  get isEmpty(): boolean {
    return this.items.length === 0;
  }

  /** Whether to offer the "New chat" action: there's a conversation to clear, or
   *  a connection error to recover from — the error banner points at it, so it
   *  must be reachable even on an empty transcript (NG-1269). */
  get showNewChat(): boolean {
    return !this.isEmpty || this.connectionStatus === "error";
  }

  /* ---------------------------------------------------------------- actions */

  openSession(workspaceSlug: string): void {
    // A blank slug can't address the agent, and tearing a healthy connection
    // down to dial one is exactly how NG-1269's stuck-connecting state arises.
    if (!workspaceSlug) return;
    if (this.connectionStatus !== "disconnected") return;
    this.connectionStatus = "connecting";
    this.authFailed = false;
    this.connectingSlug = workspaceSlug;
    this.connectAttempts = 0;
    this.connectAttempt();
  }

  /** Open the socket for {@link connectingSlug} and arm the ready-timeout. */
  private connectAttempt(): void {
    const slug = this.connectingSlug;
    if (!slug) return;
    this.connectAttempts += 1;
    this.rootStore.reportingAgentSocket.connect(slug, {
      onReady: this.handleReady,
      onDelta: this.handleDelta,
      onAssistantMessage: this.handleAssistantMessage,
      onToolStarted: this.handleToolStarted,
      onInputRequest: this.handleInputRequest,
      onApprovalRequest: this.handleApprovalRequest,
      onDashboardReady: this.handleDashboardReady,
      onTurnComplete: this.handleTurnComplete,
      onTurnError: this.handleTurnError,
      onAuthFailed: this.handleAuthFailed,
      onError: this.handleConnectionError,
    });
    this.armConnectTimeout();
  }

  private armConnectTimeout(): void {
    this.clearConnectTimeout();
    this.connectTimer = setTimeout(this.handleConnectTimeout, CONNECT_READY_TIMEOUT_MS);
  }

  private clearConnectTimeout(): void {
    if (this.connectTimer !== null) {
      clearTimeout(this.connectTimer);
      this.connectTimer = null;
    }
  }

  /** `ready` never arrived. Redial on a fresh socket (which almost always gets
   *  the frame the stuck one lost), or give up with an error once retries run
   *  out — so the user sees the banner instead of a permanently dead composer. */
  private handleConnectTimeout(): void {
    runInAction(() => {
      // Resolved (connected / errored / torn down) between arming and firing.
      if (this.connectionStatus !== "connecting") return;
      this.connectTimer = null;
      if (this.connectAttempts < MAX_CONNECT_ATTEMPTS && this.connectingSlug) {
        console.warn("[reporting-agent] no ready within timeout; reconnecting", {
          attempt: this.connectAttempts,
        });
        // connectAttempt() → socket.connect() tears down the stuck socket itself.
        this.connectAttempt();
      } else {
        console.error("[reporting-agent] connect failed: no ready after retries", {
          attempts: this.connectAttempts,
        });
        // No reconnect follows, so close the stuck socket here.
        this.rootStore.reportingAgentSocket.disconnect();
        this.connectingSlug = null;
        this.connectionStatus = "error";
        this.status = "failed";
      }
    });
  }

  closeSession(): void {
    this.rootStore.reportingAgentSocket.disconnect();
    this.reset();
  }

  /** Close and reopen — the "new chat" / clear action. */
  restartSession(workspaceSlug: string): void {
    this.closeSession();
    this.openSession(workspaceSlug);
  }

  /** Load the caller's past conversations for the history picker. Best-effort:
   *  a failure leaves the current list and just warns. */
  async loadConversations(workspaceSlug: string): Promise<void> {
    this.conversationsLoading = true;
    try {
      const items = await this.rootStore.reportingAgentService.listConversations(workspaceSlug);
      runInAction(() => {
        this.conversations = items;
      });
    } catch (error) {
      console.warn("[reporting-agent] failed to load conversation history", error);
    } finally {
      runInAction(() => {
        this.conversationsLoading = false;
      });
    }
  }

  /** Reopen a past conversation: fetch + project its transcript into the view,
   *  then connect and `resume` it so new turns continue the same conversation. */
  async resumeSession(workspaceSlug: string, sessionId: string): Promise<void> {
    let history: ReportingTranscriptItem[];
    try {
      const detail = await this.rootStore.reportingAgentService.getConversation(
        workspaceSlug,
        sessionId,
      );
      history = projectTranscript(detail.messages);
    } catch (error) {
      console.error("[reporting-agent] failed to load conversation to resume", sessionId, error);
      return;
    }
    // Tear down the current session, seed the transcript, then connect + resume.
    this.closeSession();
    runInAction(() => {
      this.items = history;
      this.pendingResumeId = sessionId;
    });
    this.openSession(workspaceSlug);
  }

  sendMessage(text: string): void {
    const trimmed = text.trim();
    if (!trimmed || this.inputDisabled) return;
    this.items.push({ kind: "message", id: crypto.randomUUID(), role: "user", content: trimmed });
    this.status = "updating";
    this.rootStore.reportingAgentSocket.sendUserMessage(trimmed);
  }

  /** Answer an inline question box: send the reply and record it on the box (which
   *  stays in place, now read-only). */
  respondToInput(toolCallId: string, text: string): void {
    this.rootStore.reportingAgentSocket.sendInputResponse(toolCallId, text);
    const item = this.items.find(
      (i): i is ReportingInputRequestItem =>
        i.kind === "input_request" && i.toolCallId === toolCallId && i.answer === null,
    );
    if (item) item.answer = text;
    // The turn resumes after the reply; keep it visibly in progress.
    this.status = "updating";
  }

  /** Decide an inline plan box: send the verdict and record it on the box. */
  respondToApproval(toolCallId: string, approved: boolean, note?: string): void {
    this.rootStore.reportingAgentSocket.sendApprovalResponse(toolCallId, approved, note);
    const item = this.items.find(
      (i): i is ReportingApprovalRequestItem =>
        i.kind === "approval_request" && i.toolCallId === toolCallId && i.approved === null,
    );
    if (item) {
      item.approved = approved;
      item.note = note ?? null;
    }
    this.status = "updating";
  }

  /* --------------------------------------------------------- socket handlers */

  private handleReady(sessionId: string): void {
    runInAction(() => {
      // Connected — cancel the retry clock and reset the attempt counter.
      this.clearConnectTimeout();
      this.connectAttempts = 0;
      this.connectingSlug = null;
      this.connectionStatus = "connected";
      this.sessionId = sessionId;
      if (this.pendingResumeId && sessionId !== this.pendingResumeId) {
        // Fresh session minted on connect — ask the server to load the chosen one.
        this.rootStore.reportingAgentSocket.sendResume(this.pendingResumeId);
      } else if (this.pendingResumeId && sessionId === this.pendingResumeId) {
        // The resumed conversation is now bound; nothing more to do.
        this.pendingResumeId = null;
      }
    });
  }

  private handleDelta(text: string): void {
    runInAction(() => {
      // Streaming resumed — the cursor takes over from the tool indicator.
      this.toolRunning = false;
      if (!this.streamingMessageId) {
        const id = crypto.randomUUID();
        this.items.push({ kind: "message", id, role: "agent", content: text });
        this.streamingMessageId = id;
      } else {
        const msg = this.streamingMessage();
        if (msg) msg.content += text;
      }
    });
  }

  private handleAssistantMessage(text: string): void {
    runInAction(() => {
      this.toolRunning = false;
      const streaming = this.streamingMessage();
      if (streaming) {
        // Replace the streamed bubble with the canonical full text.
        streaming.content = text;
      } else {
        this.items.push({ kind: "message", id: crypto.randomUUID(), role: "agent", content: text });
      }
    });
  }

  private handleToolStarted(): void {
    runInAction(() => {
      this.toolRunning = true;
    });
  }

  private handleInputRequest(toolCallId: string, prompt: string, options: string[] | null): void {
    runInAction(() => {
      this.toolRunning = false;
      // The box is a boundary: end the current streamed message so the agent's
      // reply after it starts a new message *below* the box, not appended above.
      this.streamingMessageId = undefined;
      // Append the question inline (pending); it stays once answered.
      this.items.push({
        kind: "input_request",
        id: crypto.randomUUID(),
        toolCallId,
        prompt,
        options,
        answer: null,
      });
    });
  }

  private handleApprovalRequest(
    toolCallId: string,
    summary: string,
    body: string | null,
    risk: string | null,
  ): void {
    runInAction(() => {
      this.toolRunning = false;
      // The box is a boundary — see handleInputRequest: end the current streamed
      // message so the agent's continuation renders below the box, not above it.
      this.streamingMessageId = undefined;
      this.items.push({
        kind: "approval_request",
        id: crypto.randomUUID(),
        toolCallId,
        summary,
        body,
        risk,
        approved: null,
        note: null,
      });
    });
  }

  private handleDashboardReady(artifactId: string): void {
    if (!artifactId) return;
    // The Reports list on hand predates this report; the next visit refetches
    // (NG-1948). The list store keeps its rows until the fresh answer lands.
    this.rootStore.reportingAgentDashboards.invalidate();
    runInAction(() => {
      const turnInProgress = this.status === "updating" || !!this.streamingMessageId;
      const target = turnInProgress ? undefined : this.lastAgentMessage();
      if (target) {
        target.dashboardArtifactId = artifactId;
      } else {
        this.pendingDashboardArtifactId = artifactId;
      }
    });
  }

  private handleTurnComplete(): void {
    runInAction(() => {
      if (this.pendingDashboardArtifactId) {
        const target = this.lastAgentMessage();
        if (target) target.dashboardArtifactId = this.pendingDashboardArtifactId;
        this.pendingDashboardArtifactId = null;
      }
      this.streamingMessageId = undefined;
      this.toolRunning = false;
      this.status = "success";
    });
  }

  private handleTurnError(): void {
    runInAction(() => {
      this.streamingMessageId = undefined;
      this.toolRunning = false;
      this.pendingDashboardArtifactId = null;
      this.status = "failed";
    });
  }

  private handleAuthFailed(): void {
    runInAction(() => {
      this.clearConnectTimeout();
      this.connectingSlug = null;
      this.connectionStatus = "error";
      this.authFailed = true;
      this.status = "failed";
    });
  }

  private handleConnectionError(): void {
    runInAction(() => {
      this.clearConnectTimeout();
      this.connectingSlug = null;
      this.connectionStatus = "error";
      this.streamingMessageId = undefined;
      this.toolRunning = false;
      this.pendingDashboardArtifactId = null;
      this.status = "failed";
    });
  }

  /** The in-flight streaming message item, if any. */
  private streamingMessage(): ReportingMessageItem | undefined {
    if (!this.streamingMessageId) return undefined;
    const found = this.items.find(
      (item) => item.kind === "message" && item.id === this.streamingMessageId,
    );
    return found?.kind === "message" ? found : undefined;
  }

  private lastAgentMessage(): ReportingMessageItem | undefined {
    const streaming = this.streamingMessage();
    if (streaming) return streaming;
    for (let i = this.items.length - 1; i >= 0; i--) {
      const item = this.items[i];
      if (item.kind === "message" && item.role === "agent") return item;
    }
    return undefined;
  }

  private reset(): void {
    this.clearConnectTimeout();
    this.connectAttempts = 0;
    this.connectingSlug = null;
    this.connectionStatus = "disconnected";
    this.sessionId = undefined;
    this.items = [];
    this.streamingMessageId = undefined;
    this.toolRunning = false;
    this.status = "idle";
    this.pendingDashboardArtifactId = null;
    this.pendingResumeId = null;
    this.authFailed = false;
    // `conversations` is a cache of past chats — intentionally preserved across
    // restart/resume so the history picker doesn't reload on every "new chat".
  }
}
